import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Configuration, TasksApi, PersonaGroupsApi } from '../BackendApiClient';

interface TaskResponse {
  taskId: number;
  taskType: string;
  taskParams: any;
  recordCreationTime: string;
}

interface SleepPeriod {
  startOffsetSeconds: number;
  endOffsetSeconds: number;
  localStart: string;
  localEnd: string;
}

interface ScheduledTask {
  task_id: number;
  min_duration_seconds?: number;
  max_duration_seconds?: number;
  duration_seconds?: number;
  total_duration_seconds?: number;
  number_of_tasks: number;
  user_ids?: string[];
}

interface ScheduledBlock {
  scheduled_tasks: ScheduledTask[];
  block_duration_seconds: number;
  tasks_order_type: 'sequential' | 'random';
  is_sleep?: boolean;
}

interface ScheduleGroup {
  scheduled_blocks: ScheduledBlock[];
  total_duration_seconds: number;
  blocks_order_type: 'sequential' | 'random';
  variance_percentage: number;
  use_queue?: boolean;
}

interface TimeSlot {
  type: 'sleep' | 'activity';
  startOffsetSeconds: number;
  endOffsetSeconds: number;
  durationSeconds: number;
  localStart: string;
  localEnd: string;
  sleepTaskId?: number;
  activityBlocks: ScheduledBlock[];
}

interface Props {
  groups: any[];
  onClose: () => void;
  onSubmit: (groupId: number, schedule: ScheduleGroup) => void;
  loading: boolean;
}

const DAY_SECONDS = 24 * 60 * 60;
const DEFAULT_MIN_TASK_SECONDS = 5 * 60;
const DEFAULT_MAX_TASK_SECONDS = 30 * 60;
const DEFAULT_SLEEP_MIN_SECONDS = 2 * 60;
const DEFAULT_SLEEP_MAX_SECONDS = 90 * 60;

const createSleepTaskResponse = (taskId: number): TaskResponse => ({
  taskId,
  taskType: 'sleep',
  taskParams: {},
  recordCreationTime: '',
});

const findSleepTask = (tasks: TaskResponse[], sleepTaskId?: number) =>
  (sleepTaskId ? tasks.find(t => t.taskId === sleepTaskId) : undefined) ||
  tasks.find(t => t.taskType === 'sleep') ||
  (sleepTaskId ? createSleepTaskResponse(sleepTaskId) : undefined);

const createDefaultTask = (task: TaskResponse): ScheduledTask => ({
  task_id: task.taskId,
  min_duration_seconds: DEFAULT_MIN_TASK_SECONDS,
  max_duration_seconds: DEFAULT_MAX_TASK_SECONDS,
  number_of_tasks: 1,
});

const createFixedTask = (task: TaskResponse, durationSeconds: number): ScheduledTask => ({
  task_id: task.taskId,
  duration_seconds: durationSeconds,
  number_of_tasks: 1,
});

const createQuotaTask = (
  task: TaskResponse,
  totalDurationSeconds: number,
  minDurationSeconds: number,
  maxDurationSeconds: number,
): ScheduledTask => ({
  task_id: task.taskId,
  min_duration_seconds: Math.min(minDurationSeconds, totalDurationSeconds),
  max_duration_seconds: Math.min(maxDurationSeconds, totalDurationSeconds),
  total_duration_seconds: totalDurationSeconds,
  number_of_tasks: 1,
});

const createRandomMixBlock = (
  blockDurationSeconds: number,
  tasks: TaskResponse[],
  sleepTaskId?: number,
): ScheduledBlock | null => {
  const sleepTask = findSleepTask(tasks, sleepTaskId);
  const forYouTask = tasks.find(t => t.taskType === 'for_you_feed');
  const scheduled_tasks: ScheduledTask[] = [];

  if (sleepTask && forYouTask) {
    scheduled_tasks.push(createDefaultTask(sleepTask));
    scheduled_tasks.push(createDefaultTask(forYouTask));
  } else {
    const nonSleep = tasks.filter(t => t.taskType !== 'sleep');
    const defaultTask = forYouTask || nonSleep[0] || sleepTask || tasks[0];
    if (!defaultTask) return null;
    scheduled_tasks.push(createDefaultTask(defaultTask));
  }

  return {
    scheduled_tasks,
    block_duration_seconds: blockDurationSeconds,
    tasks_order_type: sleepTask && forYouTask ? 'random' : 'sequential',
  };
};

const createHumanBlocksForDuration = (
  durationSeconds: number,
  tikTokSeconds: number,
  sleepTask: TaskResponse | undefined,
  forYouTask: TaskResponse | undefined,
): ScheduledBlock[] => {
  const clampedTikTokSeconds = Math.min(durationSeconds, Math.max(0, tikTokSeconds));
  const sleepSeconds = durationSeconds - clampedTikTokSeconds;
  const scheduled_tasks: ScheduledTask[] = [];

  if (sleepTask && sleepSeconds > 0) {
    scheduled_tasks.push(createQuotaTask(
      sleepTask,
      sleepSeconds,
      DEFAULT_SLEEP_MIN_SECONDS,
      DEFAULT_SLEEP_MAX_SECONDS,
    ));
  }

  if (forYouTask && clampedTikTokSeconds > 0) {
    scheduled_tasks.push(createQuotaTask(
      forYouTask,
      clampedTikTokSeconds,
      DEFAULT_MIN_TASK_SECONDS,
      DEFAULT_MAX_TASK_SECONDS,
    ));
  }

  return scheduled_tasks.length > 0
    ? [{
        scheduled_tasks,
        block_duration_seconds: durationSeconds,
        tasks_order_type: 'random',
      }]
    : [];
};

const autofillActivityBlocks = (
  slots: TimeSlot[],
  tasks: TaskResponse[],
  sleepTaskId: number | undefined,
  researchMode: boolean,
  dailyTikTokHours: number,
): TimeSlot[] => {
  const activitySecondsByDay = new Map<number, number>();
  const activitySegmentsByDay = new Map<number, Array<{ start: number; end: number }>>();
  const usedTikTokSecondsByDay = new Map<number, number>();
  const dailyTikTokBudgetSeconds = Math.max(0, Math.round(dailyTikTokHours * 60 * 60));
  const sleepTask = findSleepTask(tasks, sleepTaskId);
  const forYouTask = tasks.find(t => t.taskType === 'for_you_feed');

  slots.forEach(slot => {
    if (slot.type !== 'activity') return;

    let cursor = slot.startOffsetSeconds;
    while (cursor < slot.endOffsetSeconds) {
      const dayIndex = Math.floor(cursor / DAY_SECONDS);
      const dayEnd = Math.min(slot.endOffsetSeconds, (dayIndex + 1) * DAY_SECONDS);
      activitySecondsByDay.set(
        dayIndex,
        (activitySecondsByDay.get(dayIndex) || 0) + dayEnd - cursor,
      );
      activitySegmentsByDay.set(
        dayIndex,
        [...(activitySegmentsByDay.get(dayIndex) || []), { start: cursor, end: dayEnd }],
      );
      cursor = dayEnd;
    }
  });

  const remainingActivityFrom = (dayIndex: number, offsetSeconds: number) =>
    (activitySegmentsByDay.get(dayIndex) || []).reduce((sum, segment) => {
      if (segment.end <= offsetSeconds) return sum;
      return sum + segment.end - Math.max(segment.start, offsetSeconds);
    }, 0);

  return slots.map(slot => {
    if (slot.type !== 'activity') return slot;

    const activityBlocks = researchMode
      ? (() => {
          const block = createRandomMixBlock(slot.durationSeconds, tasks, sleepTaskId);
          return block ? [block] : [];
        })()
      : (() => {
          const blocks: ScheduledBlock[] = [];
          let cursor = slot.startOffsetSeconds;

          while (cursor < slot.endOffsetSeconds) {
            const dayIndex = Math.floor(cursor / DAY_SECONDS);
            const dayEnd = Math.min(slot.endOffsetSeconds, (dayIndex + 1) * DAY_SECONDS);
            const chunkDuration = dayEnd - cursor;
            const dayActivitySeconds = activitySecondsByDay.get(dayIndex) || chunkDuration;
            const dayTikTokBudget = Math.min(dailyTikTokBudgetSeconds, dayActivitySeconds);
            const usedToday = usedTikTokSecondsByDay.get(dayIndex) || 0;
            const remainingTikTokToday = Math.max(0, dayTikTokBudget - usedToday);
            const remainingActivityToday = Math.max(1, remainingActivityFrom(dayIndex, cursor));
            const isLastChunkOfDay = remainingActivityFrom(dayIndex, dayEnd) <= 0;
            const tikTokForChunk = isLastChunkOfDay
              ? Math.min(chunkDuration, remainingTikTokToday)
              : Math.min(
                  chunkDuration,
                  remainingTikTokToday,
                  Math.round((remainingTikTokToday * chunkDuration) / remainingActivityToday),
                );

            blocks.push(...createHumanBlocksForDuration(chunkDuration, tikTokForChunk, sleepTask, forYouTask));
            usedTikTokSecondsByDay.set(dayIndex, usedToday + tikTokForChunk);
            cursor = dayEnd;
          }

          return blocks;
        })();

    return {
      ...slot,
      activityBlocks,
    };
  });
};

const apiConfig = new Configuration({ basePath: '' });
const tasksApi = new TasksApi(apiConfig);
const personaGroupsApi = new PersonaGroupsApi(apiConfig);

const AdvancedScheduleBuilder: React.FC<Props> = ({ groups, onClose, onSubmit, loading }) => {
  const [selectedGroupId, setSelectedGroupId] = useState<number>(0);
  const [availableTasks, setAvailableTasks] = useState<TaskResponse[]>([]);
  const [totalDays, setTotalDays] = useState<number>(3);
  const [sleepStartHour, setSleepStartHour] = useState(0);
  const [sleepEndHour, setSleepEndHour] = useState(7);
  const [sleepVariance, setSleepVariance] = useState(30);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [generatingSleep, setGeneratingSleep] = useState(false);
  const [groupDetails, setGroupDetails] = useState<any>(null);
  const [blocksOrderType, setBlocksOrderType] = useState<'sequential' | 'random'>('sequential');
  const [useQueue, setUseQueue] = useState(false);
  const [variance, setVariance] = useState(20);
  const [defaultBlockDuration, setDefaultBlockDuration] = useState(120); // Default 2 hours in minutes
  const [researchMode, setResearchMode] = useState(false);
  const [dailyTikTokHours, setDailyTikTokHours] = useState(3);

  useEffect(() => {
    loadTasks();
  }, []);

  useEffect(() => {
    if (selectedGroupId) {
      loadGroupDetails(selectedGroupId);
    } else {
      setGroupDetails(null);
      setTimeSlots([]);
    }
  }, [selectedGroupId]);

  const loadGroupDetails = async (groupId: number) => {
    try {
      const data = await personaGroupsApi.getPersonaGroupUsersApiPersonaGroupsGroupIdUsersGet({ groupId });
      setGroupDetails(data);
    } catch (err) {
      console.error('Failed to load group details:', err);
    }
  };

  const loadTasks = async () => {
    try {
      const tasks = await tasksApi.getTasksApiTasksGet();
      setAvailableTasks(tasks as unknown as TaskResponse[]);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    }
  };

  // Extract all unique regions from group
  const groupRegions = useMemo(() => {
    if (!groupDetails?.users?.length) return [];
    const regionMap = new Map<number, { regionId: number; name: string; timezone: string; userCount: number }>();
    
    for (const user of groupDetails.users) {
      if (user.region) {
        const existing = regionMap.get(user.region.regionId);
        if (existing) {
          existing.userCount++;
        } else {
          regionMap.set(user.region.regionId, {
            regionId: user.region.regionId,
            name: user.region.carrierRegion,
            timezone: user.region.timezoneName,
            userCount: 1,
          });
        }
      }
    }
    return Array.from(regionMap.values());
  }, [groupDetails]);

  const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null);
  
  // Auto-select region if there's only one
  useEffect(() => {
    if (groupRegions.length === 1) {
      setSelectedRegionId(groupRegions[0].regionId);
    } else if (groupRegions.length === 0) {
      setSelectedRegionId(null);
    }
  }, [groupRegions]);

  const groupRegion = useMemo(() => {
    if (!selectedRegionId) return null;
    return groupRegions.find(r => r.regionId === selectedRegionId) || null;
  }, [selectedRegionId, groupRegions]);

  // Auto-generate sleep blocks when group or duration changes
  const generateSchedule = useCallback(async () => {
    if (!groupRegion || totalDays <= 0) return;
    
    setGeneratingSleep(true);
    try {
      const totalSeconds = totalDays * 24 * 60 * 60;
      const response = await tasksApi.generateSleepBlocksApiTasksGenerateSleepBlocksPost({
        sleepScheduleRequest: {
          regionId: groupRegion.regionId,
          totalDurationSeconds: totalSeconds,
          sleepStartHour,
          sleepEndHour,
          varianceMinutes: sleepVariance,
        }
      });
      
      const sleepPeriods: SleepPeriod[] = (response.sleepPeriods || []).map((p: any) => ({
        startOffsetSeconds: p.startOffsetSeconds,
        endOffsetSeconds: p.endOffsetSeconds,
        localStart: p.localStart,
        localEnd: p.localEnd,
      }));

      // Get sleep task ID from the response blocks
      const sleepTaskId = response.blocks?.[0]?.scheduledTasks?.[0]?.taskId;
      let tasksForAutofill = availableTasks;
      if (sleepTaskId && !tasksForAutofill.some(t => t.taskId === sleepTaskId)) {
        tasksForAutofill = [...tasksForAutofill, createSleepTaskResponse(sleepTaskId)];
        setAvailableTasks(tasksForAutofill);
      }
      
      // Parse local time from API format: "2025-12-24 15:00" -> "12-24 15:00"
      const parseLocalTime = (timeStr: string) => timeStr.replace(/^\d{4}-/, '');

      // Build time slots: derive activity times from sleep boundaries for consistency
      const slots: TimeSlot[] = [];
      let currentOffset = 0;

      for (let i = 0; i < sleepPeriods.length; i++) {
        const sleep = sleepPeriods[i];
        
        // Activity slot before this sleep
        if (sleep.startOffsetSeconds > currentOffset) {
          const activityStart = i === 0 ? 'Now' : parseLocalTime(sleepPeriods[i-1].localEnd);
          slots.push({
            type: 'activity',
            startOffsetSeconds: currentOffset,
            endOffsetSeconds: sleep.startOffsetSeconds,
            durationSeconds: sleep.startOffsetSeconds - currentOffset,
            localStart: activityStart,
            localEnd: parseLocalTime(sleep.localStart),
            activityBlocks: [],
          });
        }
        
        // Sleep slot
        slots.push({
          type: 'sleep',
          startOffsetSeconds: sleep.startOffsetSeconds,
          endOffsetSeconds: sleep.endOffsetSeconds,
          durationSeconds: sleep.endOffsetSeconds - sleep.startOffsetSeconds,
          localStart: parseLocalTime(sleep.localStart),
          localEnd: parseLocalTime(sleep.localEnd),
          sleepTaskId,
          activityBlocks: [],
        });
        
        currentOffset = sleep.endOffsetSeconds;
      }
      
      // Final activity slot after last sleep
      if (currentOffset < totalSeconds) {
        const lastSleepEnd = sleepPeriods.length > 0 ? 
          parseLocalTime(sleepPeriods[sleepPeriods.length - 1].localEnd) : 'Now';
        const remainingHours = Math.floor((totalSeconds - currentOffset) / 3600);
        slots.push({
          type: 'activity',
          startOffsetSeconds: currentOffset,
          endOffsetSeconds: totalSeconds,
          durationSeconds: totalSeconds - currentOffset,
          localStart: lastSleepEnd,
          localEnd: `+${remainingHours}h`,
          activityBlocks: [],
        });
      }
      
      setTimeSlots(autofillActivityBlocks(slots, tasksForAutofill, sleepTaskId, researchMode, dailyTikTokHours));
    } catch (err) {
      console.error('Failed to generate schedule:', err);
      alert('Failed to generate sleep schedule');
    } finally {
      setGeneratingSleep(false);
    }
  }, [groupRegion, totalDays, sleepStartHour, sleepEndHour, sleepVariance, availableTasks, researchMode, dailyTikTokHours]);

  // Regenerate when parameters change
  useEffect(() => {
    if (groupRegion && totalDays > 0) {
      generateSchedule();
    }
  }, [groupRegion, totalDays, sleepStartHour, sleepEndHour, sleepVariance, researchMode, dailyTikTokHours, availableTasks, generateSchedule]);

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remHours = hours % 24;
      return `${days}d ${remHours}h`;
    }
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const isForYouTaskId = (taskId: number) =>
    availableTasks.some(t => t.taskId === taskId && t.taskType === 'for_you_feed');

  const getAllocatedForYouSecondsForDay = (dayIndex: number) => timeSlots.reduce((slotSum, currentSlot) => {
    if (currentSlot.type !== 'activity' || Math.floor(currentSlot.startOffsetSeconds / DAY_SECONDS) !== dayIndex) {
      return slotSum;
    }
    return slotSum + currentSlot.activityBlocks.reduce((blockSum, block) => blockSum + block.scheduled_tasks.reduce(
      (taskSum, task) => taskSum + (isForYouTaskId(task.task_id)
        ? (task.total_duration_seconds ?? block.block_duration_seconds)
        : 0),
      0,
    ), 0);
  }, 0);

  const addBlockToSlot = (slotIndex: number) => {
    if (!availableTasks.length) {
      alert('No tasks available. Please create tasks first.');
      return;
    }
    const slot = timeSlots[slotIndex];
    if (slot.type === 'sleep') return;
    
    // Find sleep and for_you tasks
    const sleepTask = availableTasks.find(t => t.taskType === 'sleep');
    const forYouTask = availableTasks.find(t => t.taskType === 'for_you_feed');
    const hasBothSleepAndForYou = sleepTask && forYouTask;
    
    const newSlots = [...timeSlots];
    
    // Calculate already scheduled time in this slot
    const alreadyScheduled = slot.activityBlocks.reduce((sum, b) => sum + b.block_duration_seconds, 0);
    const remainingTime = slot.durationSeconds - alreadyScheduled;
    
    // Fill the remaining time in the slot by default (or use default block duration if no blocks yet)
    const blockDurationSeconds = slot.activityBlocks.length === 0 
      ? slot.durationSeconds  // First block fills the entire activity slot
      : Math.min(remainingTime, defaultBlockDuration * 60);  // Subsequent blocks use remaining or default
    
    if (blockDurationSeconds <= 0) {
      alert('No remaining time in this activity slot. Adjust existing block durations to add more blocks.');
      return;
    }
    
    // Build scheduled tasks - if both sleep and for_you exist, add both
    const scheduled_tasks: ScheduledTask[] = [];
    if (hasBothSleepAndForYou) {
      const dayIndex = Math.floor(slot.startOffsetSeconds / DAY_SECONDS);
      const allocatedForYouSeconds = getAllocatedForYouSecondsForDay(dayIndex);
      const remainingDailySeconds = Math.max(0, Math.round(dailyTikTokHours * 60 * 60) - allocatedForYouSeconds);
      const forYouTotalSeconds = remainingDailySeconds >= DEFAULT_MIN_TASK_SECONDS
        ? Math.min(blockDurationSeconds, remainingDailySeconds)
        : 0;
      const sleepTotalSeconds = blockDurationSeconds - forYouTotalSeconds;
      if (sleepTotalSeconds > 0) {
        scheduled_tasks.push(createQuotaTask(
          sleepTask,
          sleepTotalSeconds,
          DEFAULT_SLEEP_MIN_SECONDS,
          DEFAULT_SLEEP_MAX_SECONDS,
        ));
      }
      if (forYouTotalSeconds > 0) {
        scheduled_tasks.push(createQuotaTask(
          forYouTask,
          forYouTotalSeconds,
          DEFAULT_MIN_TASK_SECONDS,
          DEFAULT_MAX_TASK_SECONDS,
        ));
      }
    } else {
      // Fall back to first non-sleep task
      const nonSleep = availableTasks.filter(t => t.taskType !== 'sleep');
      const defaultTask = nonSleep.length > 0 ? nonSleep[0] : availableTasks[0];
      scheduled_tasks.push({
        task_id: defaultTask.taskId,
        min_duration_seconds: 300,
        max_duration_seconds: 1800,
        number_of_tasks: 1
      });
    }
    
    newSlots[slotIndex].activityBlocks.push({
      scheduled_tasks,
      block_duration_seconds: blockDurationSeconds,
      tasks_order_type: hasBothSleepAndForYou ? 'random' : 'sequential'
    });
    setTimeSlots(newSlots);
  };

  const removeBlockFromSlot = (slotIndex: number, blockIndex: number) => {
    const newSlots = [...timeSlots];
    newSlots[slotIndex].activityBlocks.splice(blockIndex, 1);
    setTimeSlots(newSlots);
  };

  const updateBlockInSlot = (slotIndex: number, blockIndex: number, updates: Partial<ScheduledBlock>) => {
    const newSlots = [...timeSlots];
    newSlots[slotIndex].activityBlocks[blockIndex] = {
      ...newSlots[slotIndex].activityBlocks[blockIndex],
      ...updates
    };
    setTimeSlots(newSlots);
  };

  const addTaskToBlock = (slotIndex: number, blockIndex: number) => {
    if (!availableTasks.length) return;
    const newSlots = [...timeSlots];
    // Default to first non-sleep task, or first task if all are sleep
    const nonSleep = availableTasks.filter(t => t.taskType !== 'sleep');
    const defaultTask = nonSleep.length > 0 ? nonSleep[0] : availableTasks[0];
    newSlots[slotIndex].activityBlocks[blockIndex].scheduled_tasks.push({
      task_id: defaultTask.taskId,
      min_duration_seconds: 300,
      max_duration_seconds: 1800,
      number_of_tasks: 1
    });
    setTimeSlots(newSlots);
  };

  const removeTaskFromBlock = (slotIndex: number, blockIndex: number, taskIndex: number) => {
    const newSlots = [...timeSlots];
    newSlots[slotIndex].activityBlocks[blockIndex].scheduled_tasks.splice(taskIndex, 1);
    setTimeSlots(newSlots);
  };

  const updateTaskInBlock = (slotIndex: number, blockIndex: number, taskIndex: number, updates: Partial<ScheduledTask>) => {
    const newSlots = [...timeSlots];
    newSlots[slotIndex].activityBlocks[blockIndex].scheduled_tasks[taskIndex] = {
      ...newSlots[slotIndex].activityBlocks[blockIndex].scheduled_tasks[taskIndex],
      ...updates
    };
    setTimeSlots(newSlots);
  };

  const removeSleepSlot = (slotIndex: number) => {
    const newSlots = [...timeSlots];
    const removed = newSlots[slotIndex];
    
    // Merge with adjacent activity slots
    if (slotIndex > 0 && newSlots[slotIndex - 1].type === 'activity') {
      newSlots[slotIndex - 1].endOffsetSeconds = removed.endOffsetSeconds;
      newSlots[slotIndex - 1].durationSeconds = newSlots[slotIndex - 1].endOffsetSeconds - newSlots[slotIndex - 1].startOffsetSeconds;
      newSlots[slotIndex - 1].localEnd = removed.localEnd;
    }
    
    newSlots.splice(slotIndex, 1);
    
    // Merge consecutive activity slots
    const merged: TimeSlot[] = [];
    for (const slot of newSlots) {
      if (merged.length > 0 && merged[merged.length - 1].type === 'activity' && slot.type === 'activity') {
        merged[merged.length - 1].endOffsetSeconds = slot.endOffsetSeconds;
        merged[merged.length - 1].durationSeconds = merged[merged.length - 1].endOffsetSeconds - merged[merged.length - 1].startOffsetSeconds;
        merged[merged.length - 1].localEnd = slot.localEnd;
        merged[merged.length - 1].activityBlocks.push(...slot.activityBlocks);
      } else {
        merged.push(slot);
      }
    }
    setTimeSlots(merged);
  };

  const handleSubmit = () => {
    if (!selectedGroupId) {
      alert('Please select a group');
      return;
    }
    
    // Build final blocks list: combine all activity blocks + sleep blocks
    const allBlocks: ScheduledBlock[] = [];
    
    for (const slot of timeSlots) {
      if (slot.type === 'sleep' && slot.sleepTaskId) {
        allBlocks.push({
          scheduled_tasks: [{
            task_id: slot.sleepTaskId,
            duration_seconds: slot.durationSeconds,
            number_of_tasks: 1
          }],
          block_duration_seconds: slot.durationSeconds,
          tasks_order_type: 'sequential',
          is_sleep: true
        });
      } else {
        allBlocks.push(...slot.activityBlocks);
      }
    }
    
    const totalDuration = allBlocks.reduce((sum, b) => sum + b.block_duration_seconds, 0);
    
    const schedule: ScheduleGroup = {
      scheduled_blocks: allBlocks,
      total_duration_seconds: totalDuration,
      blocks_order_type: blocksOrderType,
      variance_percentage: variance / 100,
      use_queue: useQueue,
    };
    
    onSubmit(selectedGroupId, schedule);
  };

  const totalScheduleDuration = timeSlots.reduce((sum, s) => sum + s.durationSeconds, 0);
  const activitySlots = timeSlots.filter(s => s.type === 'activity');
  const sleepSlots = timeSlots.filter(s => s.type === 'sleep');
  const forYouTaskIds = new Set(availableTasks.filter(t => t.taskType === 'for_you_feed').map(t => t.taskId));
  const totalTikTokScheduled = timeSlots.reduce(
    (slotSum, slot) => slotSum + slot.activityBlocks.reduce(
      (blockSum, block) => blockSum + block.scheduled_tasks.reduce(
        (taskSum, task) => taskSum + (forYouTaskIds.has(task.task_id)
          ? (task.total_duration_seconds ?? block.block_duration_seconds)
          : 0),
        0,
      ),
      0,
    ),
    0,
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }}>
      <div className="glass-card" style={{width: '95%', maxWidth: '1400px', padding: '32px', maxHeight: '90vh', overflow: 'auto'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px'}}>
          <div>
            <h2 style={{fontSize: '28px', fontWeight: 'bold', color: 'white', marginBottom: '8px'}}>
              📅 Schedule Builder
            </h2>
            <p style={{fontSize: '14px', color: 'rgba(255,255,255,0.7)'}}>
              Sleep blocks and default activity blocks are auto-generated for the selected duration.
            </p>
          </div>
          <button onClick={onClose} className="btn-secondary" style={{padding: '8px 16px'}}>✕ Close</button>
        </div>

        {/* Configuration Row */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '16px', marginBottom: '24px', padding: '20px',
          background: 'rgba(255,255,255,0.05)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <div>
            <label style={{fontSize: '12px', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '4px'}}>Group</label>
            <select value={selectedGroupId} onChange={(e) => setSelectedGroupId(+e.target.value)} className="persona-input" style={{width: '100%'}}>
              <option value={0}>Select group...</option>
              {groups.map(g => (
                <option key={(g as any).group_id} value={(g as any).group_id}>
                  {(g as any).name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{fontSize: '12px', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '4px'}}>Duration (days)</label>
            <input type="number" value={totalDays} onChange={(e) => setTotalDays(Math.max(1, +e.target.value))} className="persona-input" min="1" max="30" />
          </div>
          <div>
            <label style={{fontSize: '12px', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '4px'}}>Research Mode</label>
            <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'rgba(255,255,255,0.9)', minHeight: '40px'}}>
              <input
                type="checkbox"
                checked={researchMode}
                onChange={(e) => setResearchMode(e.target.checked)}
              />
              Random sleep + For You all activity slot
            </label>
          </div>
          <div>
            <label style={{fontSize: '12px', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '4px'}}>TikTok / Day (hours)</label>
            <input
              type="number"
              value={dailyTikTokHours}
              onChange={(e) => setDailyTikTokHours(Math.max(0, +e.target.value))}
              className="persona-input"
              min="0"
              max="24"
              step="0.25"
              disabled={researchMode}
            />
          </div>
          <div>
            <label style={{fontSize: '12px', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '4px'}}>Sleep Start</label>
            <select value={sleepStartHour} onChange={(e) => setSleepStartHour(+e.target.value)} className="persona-input">
              {Array.from({length: 24}, (_, i) => <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize: '12px', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '4px'}}>Sleep End</label>
            <select value={sleepEndHour} onChange={(e) => setSleepEndHour(+e.target.value)} className="persona-input">
              {Array.from({length: 24}, (_, i) => <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>)}
            </select>
          </div>
          <div>
            <label style={{fontSize: '12px', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '4px'}}>Sleep Variance (±min)</label>
            <input type="number" value={sleepVariance} onChange={(e) => setSleepVariance(+e.target.value)} className="persona-input" min="0" max="120" />
          </div>
          <div>
            <label style={{fontSize: '12px', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '4px'}}>Variance %</label>
            <input type="number" value={variance} onChange={(e) => setVariance(+e.target.value)} className="persona-input" min="0" max="50" />
          </div>
          <div>
            <label style={{fontSize: '12px', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '4px'}}>Blocks Order</label>
            <select value={blocksOrderType} onChange={(e) => setBlocksOrderType(e.target.value as 'sequential' | 'random')} className="persona-input">
              <option value="sequential">Sequential</option>
              <option value="random">Random</option>
            </select>
          </div>
          <div>
            <label style={{fontSize: '12px', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '4px'}}>Block Duration (min)</label>
            <input 
              type="number" 
              value={defaultBlockDuration} 
              onChange={(e) => setDefaultBlockDuration(Math.max(5, +e.target.value))} 
              className="persona-input" 
              min="5" 
              max="480" 
            />
          </div>
        </div>

        {/* Multiple Regions Warning & Selector */}
        {selectedGroupId > 0 && groupRegions.length > 1 && (
          <div style={{marginBottom: '16px', padding: '16px', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px', border: '1px solid rgba(245, 158, 11, 0.4)'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px'}}>
              <span style={{fontSize: '18px'}}>⚠️</span>
              <span style={{fontSize: '14px', color: '#fbbf24', fontWeight: '500'}}>
                This group has personas from {groupRegions.length} different regions
              </span>
            </div>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
              {groupRegions.map(r => (
                <button
                  key={r.regionId}
                  onClick={() => setSelectedRegionId(r.regionId)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: selectedRegionId === r.regionId 
                      ? '2px solid #6366f1' 
                      : '1px solid rgba(255,255,255,0.2)',
                    background: selectedRegionId === r.regionId 
                      ? 'rgba(99, 102, 241, 0.2)' 
                      : 'rgba(255,255,255,0.05)',
                    color: selectedRegionId === r.regionId ? '#a5b4fc' : 'rgba(255,255,255,0.7)',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  🌍 {r.name} ({r.timezone}) - {r.userCount} persona{r.userCount > 1 ? 's' : ''}
                </button>
              ))}
            </div>
            <p style={{fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '8px'}}>
              Select a region for sleep scheduling. Personas in other regions will follow this schedule.
            </p>
          </div>
        )}

        {/* Region Info */}
        {selectedGroupId > 0 && groupRegion && (
          <div style={{marginBottom: '16px', padding: '12px 16px', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '8px', border: '1px solid rgba(99, 102, 241, 0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'}}>
            <span style={{fontSize: '13px', color: '#a5b4fc'}}>
              🌍 Region: <strong>{groupRegion.name}</strong> &nbsp;|&nbsp; 
              🕐 Timezone: <strong>{groupRegion.timezone}</strong> &nbsp;|&nbsp;
              👥 {groupRegion.userCount} persona{groupRegion.userCount > 1 ? 's' : ''} &nbsp;|&nbsp;
              🧭 <strong>{researchMode ? 'Research random mix' : `Human (${dailyTikTokHours}h TikTok/day)`}</strong> &nbsp;|&nbsp;
              🌙 {sleepSlots.length} sleep periods &nbsp;|&nbsp;
              ☀️ {activitySlots.length} activity slots &nbsp;|&nbsp;
              📊 Total: {formatDuration(totalScheduleDuration)} &nbsp;|&nbsp;
              🎯 {availableTasks.length} tasks available
            </span>
            <button onClick={generateSchedule} className="btn-secondary" style={{padding: '6px 12px', fontSize: '12px'}} disabled={generatingSleep}>
              🔄 Regenerate
            </button>
          </div>
        )}

        {generatingSleep && (
          <div style={{textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.7)'}}>
            <div style={{fontSize: '24px', marginBottom: '8px'}}>⏳</div>
            Generating schedule...
          </div>
        )}

        {/* Time Slots */}
        {!generatingSleep && timeSlots.length > 0 && (
          <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
            {timeSlots.map((slot, slotIdx) => (
              <div key={slotIdx} style={{
                background: slot.type === 'sleep' 
                  ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(99, 102, 241, 0.1))'
                  : 'rgba(255,255,255,0.05)',
                border: `1px solid ${slot.type === 'sleep' ? 'rgba(139, 92, 246, 0.4)' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '12px', padding: '16px'
              }}>
                {/* Slot Header */}
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: slot.type === 'activity' ? '12px' : '0'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <span style={{fontSize: '20px'}}>{slot.type === 'sleep' ? '🌙' : '☀️'}</span>
                    <div>
                      <div style={{fontSize: '14px', fontWeight: '600', color: slot.type === 'sleep' ? '#a78bfa' : 'white'}}>
                        {slot.type === 'sleep' ? 'Sleep' : 'Activity'} • {formatDuration(slot.durationSeconds)}
                      </div>
                      <div style={{fontSize: '12px', color: 'rgba(255,255,255,0.5)'}}>
                        {slot.localStart} → {slot.localEnd}
                      </div>
                    </div>
                  </div>
                  {slot.type === 'sleep' && (
                    <button onClick={() => removeSleepSlot(slotIdx)} className="btn-secondary" style={{padding: '4px 10px', fontSize: '12px'}}>
                      Remove Sleep
                    </button>
                  )}
                  {slot.type === 'activity' && (() => {
                    const scheduled = slot.activityBlocks.reduce((sum: number, b: ScheduledBlock) => sum + b.block_duration_seconds, 0);
                    const available = slot.durationSeconds;
                    const diff = available - scheduled;
                    const isOver = scheduled > available;
                    const isUnder = scheduled < available;
                    const isMatch = Math.abs(diff) < 60; // Within 1 minute
                    const statusColor = isMatch ? '#4ade80' : isOver ? '#f87171' : '#fbbf24';
                    const statusIcon = isMatch ? '✓' : isOver ? '⚠️' : '📊';
                    return (
                      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <div style={{
                          padding: '4px 10px', borderRadius: '6px', fontSize: '12px',
                          background: `${statusColor}20`, border: `1px solid ${statusColor}40`, color: statusColor
                        }}>
                          {statusIcon} {formatDuration(scheduled)} / {formatDuration(available)}
                          {isUnder && !isMatch && ` (${formatDuration(diff)} remaining)`}
                          {isOver && ` (${formatDuration(-diff)} over)`}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Activity Blocks */}
                {slot.type === 'activity' && (
                  <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px'}}>
                    {slot.activityBlocks.map((block, blockIdx) => (
                      <div key={blockIdx} style={{
                        background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '12px',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px'}}>
                          <span style={{fontSize: '13px', color: 'white', fontWeight: '500'}}>Block {blockIdx + 1}</span>
                          <div style={{display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap'}}>
                            <div style={{display: 'flex', gap: '6px', alignItems: 'center'}}>
                              <label style={{fontSize: '11px', color: 'rgba(255,255,255,0.6)'}}>Duration:</label>
                              <input
                                type="number"
                                value={Math.floor(block.block_duration_seconds / 60)}
                                onChange={(e) => updateBlockInSlot(slotIdx, blockIdx, { block_duration_seconds: +e.target.value * 60 })}
                                className="persona-input"
                                style={{width: '70px', padding: '4px 8px', fontSize: '12px'}}
                                min="1"
                              />
                              <span style={{fontSize: '11px', color: 'rgba(255,255,255,0.5)'}}>min</span>
                            </div>
                            <div style={{display: 'flex', gap: '6px', alignItems: 'center'}}>
                              <label style={{fontSize: '11px', color: 'rgba(255,255,255,0.6)'}}>Tasks Order:</label>
                              <select
                                value={block.tasks_order_type}
                                onChange={(e) => updateBlockInSlot(slotIdx, blockIdx, { tasks_order_type: e.target.value as 'sequential' | 'random' })}
                                className="persona-input"
                                style={{padding: '4px 8px', fontSize: '11px', width: 'auto'}}
                              >
                                <option value="sequential">Sequential</option>
                                <option value="random">Random</option>
                              </select>
                            </div>
                            <button onClick={() => removeBlockFromSlot(slotIdx, blockIdx)} style={{
                              background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.4)',
                              color: '#f87171', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer'
                            }}>✕</button>
                          </div>
                        </div>

                        {/* Tasks in Block */}
                        <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                          {block.scheduled_tasks.map((task, taskIdx) => (
                            <div key={taskIdx} style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              background: 'rgba(255,255,255,0.08)', borderRadius: '6px', padding: '8px 12px'
                            }}>
                              <span style={{fontSize: '11px', color: 'rgba(255,255,255,0.5)', minWidth: '20px'}}>#{taskIdx + 1}</span>
                              <select
                                value={task.task_id}
                                onChange={(e) => updateTaskInBlock(slotIdx, blockIdx, taskIdx, { task_id: +e.target.value })}
                                className="persona-input"
                                style={{padding: '6px 10px', fontSize: '12px', flex: 1, minWidth: '200px'}}
                              >
                                {availableTasks.map(t => {
                                  const label = t.taskParams?.search_keyword
                                    || t.taskParams?.query
                                    || (t.taskParams && Object.keys(t.taskParams).length > 0
                                        ? Object.entries(t.taskParams).map(([k, v]) => `${k}: ${v}`).join(', ')
                                        : '');
                                  const icon = t.taskType === 'sleep' ? '🌙 ' : t.taskType === 'search' ? '🔍 ' : '📱 ';
                                  return (
                                    <option key={t.taskId} value={t.taskId}>
                                      {icon}{t.taskType}{label ? ` (${label})` : ''}
                                    </option>
                                  );
                                })}
                              </select>
                              <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                                <span style={{fontSize: '11px', color: 'rgba(255,255,255,0.5)'}}>Total:</span>
                                <input
                                  type="number"
                                  value={task.total_duration_seconds ? Math.floor(task.total_duration_seconds / 60) : ''}
                                  onChange={(e) => {
                                    const minutes = Number(e.target.value);
                                    updateTaskInBlock(slotIdx, blockIdx, taskIdx, {
                                      total_duration_seconds: minutes > 0 ? minutes * 60 : undefined,
                                    });
                                  }}
                                  placeholder="all"
                                  className="persona-input"
                                  style={{width: '60px', padding: '6px 8px', fontSize: '12px', textAlign: 'center'}}
                                  min="1"
                                />
                                <span style={{fontSize: '11px', color: 'rgba(255,255,255,0.5)'}}>min</span>
                              </div>
                              <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                                <span style={{fontSize: '11px', color: 'rgba(255,255,255,0.5)'}}>Session:</span>
                                <input
                                  type="number"
                                  value={task.min_duration_seconds ? Math.floor(task.min_duration_seconds / 60) : ''}
                                  onChange={(e) => updateTaskInBlock(slotIdx, blockIdx, taskIdx, { min_duration_seconds: +e.target.value * 60 })}
                                  placeholder="min"
                                  className="persona-input"
                                  style={{width: '60px', padding: '6px 8px', fontSize: '12px', textAlign: 'center'}}
                                />
                                <span style={{fontSize: '11px', color: 'rgba(255,255,255,0.4)'}}>-</span>
                                <input
                                  type="number"
                                  value={task.max_duration_seconds ? Math.floor(task.max_duration_seconds / 60) : ''}
                                  onChange={(e) => updateTaskInBlock(slotIdx, blockIdx, taskIdx, { max_duration_seconds: +e.target.value * 60 })}
                                  placeholder="max"
                                  className="persona-input"
                                  style={{width: '60px', padding: '6px 8px', fontSize: '12px', textAlign: 'center'}}
                                />
                                <span style={{fontSize: '11px', color: 'rgba(255,255,255,0.5)'}}>min</span>
                              </div>
                              <button onClick={() => removeTaskFromBlock(slotIdx, blockIdx, taskIdx)} style={{
                                background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)',
                                color: '#f87171', cursor: 'pointer', fontSize: '12px', padding: '4px 8px', borderRadius: '4px'
                              }}>✕</button>
                            </div>
                          ))}
                          <button onClick={() => addTaskToBlock(slotIdx, blockIdx)} style={{
                            background: 'rgba(34, 197, 94, 0.1)', border: '1px dashed rgba(34, 197, 94, 0.4)',
                            color: '#4ade80', padding: '8px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
                            width: '100%', textAlign: 'center'
                          }}>+ Add Task</button>
                        </div>
                      </div>
                    ))}
                    
                    {/* Add Block Button at bottom */}
                    <button 
                      onClick={() => addBlockToSlot(slotIdx)} 
                      style={{
                        width: '100%', padding: '12px', background: 'rgba(34, 197, 94, 0.1)',
                        border: '2px dashed rgba(34, 197, 94, 0.4)', borderRadius: '8px',
                        color: '#4ade80', fontSize: '14px', cursor: 'pointer', marginTop: '4px',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(34, 197, 94, 0.2)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(34, 197, 94, 0.1)'; }}
                    >
                      + Add Another Block
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!generatingSleep && selectedGroupId > 0 && timeSlots.length === 0 && !groupRegion && groupRegions.length === 0 && (
          <div style={{textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)'}}>
            ⚠️ No region found for personas in this group. Please assign a region to personas first.
          </div>
        )}

        {!generatingSleep && selectedGroupId > 0 && timeSlots.length === 0 && !groupRegion && groupRegions.length > 1 && (
          <div style={{textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)'}}>
            👆 Select a region above to generate the sleep schedule.
          </div>
        )}

        {!selectedGroupId && (
          <div style={{textAlign: 'center', padding: '60px', color: 'rgba(255,255,255,0.5)'}}>
            👆 Select a group to start building a schedule
          </div>
        )}

        {/* Footer */}
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.1)', flexWrap: 'wrap', gap: '12px'}}>
          <div style={{fontSize: '13px', color: 'rgba(255,255,255,0.6)'}}>
            {timeSlots.length > 0 && (() => {
              const totalActivityTime = activitySlots.reduce((sum, s) => sum + s.durationSeconds, 0);
              const totalScheduled = timeSlots.reduce((sum, s) => sum + s.activityBlocks.reduce((bs, b) => bs + b.block_duration_seconds, 0), 0);
              const totalSleepTime = sleepSlots.reduce((sum, s) => sum + s.durationSeconds, 0);
              const diff = totalActivityTime - totalScheduled;
              return (
                <>
                  🌙 Sleep: {formatDuration(totalSleepTime)} &nbsp;|&nbsp;
                  {!researchMode && (
                    <>
                      📱 TikTok: {formatDuration(totalTikTokScheduled)} &nbsp;|&nbsp;
                    </>
                  )}
                  ☀️ Activity: {formatDuration(totalScheduled)} / {formatDuration(totalActivityTime)}
                  {diff > 60 && <span style={{color: '#fbbf24'}}> ({formatDuration(diff)} unscheduled)</span>}
                  {diff < -60 && <span style={{color: '#f87171'}}> ({formatDuration(-diff)} over)</span>}
                  {Math.abs(diff) <= 60 && <span style={{color: '#4ade80'}}> ✓</span>}
                  &nbsp;|&nbsp;
                  Blocks: {timeSlots.reduce((sum, s) => sum + s.activityBlocks.length, 0)} &nbsp;|&nbsp;
                  Sleep blocks: {sleepSlots.length}
                </>
              );
            })()}
          </div>
          <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
            <label style={{display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px'}}>
              <input
                type="checkbox"
                checked={useQueue}
                onChange={e => setUseQueue(e.target.checked)}
              />
              Schedule to Queue
            </label>
            <button onClick={onClose} className="btn-secondary">Cancel</button>
            <button
              onClick={handleSubmit}
              className="btn-primary"
              disabled={loading || !selectedGroupId || timeSlots.length === 0}
              style={{padding: '12px 32px'}}
            >
              {loading ? '⏳ Scheduling...' : (useQueue ? '🚀 Schedule to Queue' : '🚀 Schedule Group')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedScheduleBuilder;
